import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useT, useLp } from "../lib/i18n";
import { isLaunchActive } from "../lib/launch";

export function hasLaunchVideoEmbedUrl(url: string): boolean {
  return /^https:\/\/(www\.)?loom\.com\/(share|embed)\//.test(url.trim());
}

/**
 * Product Hunt launch landing page. Lives at `/launch` (and `/{lang}/launch`)
 * during the launch window. Independent from the home `/` so warm PH traffic
 * lands on a focused page with the discount + AMA in view, while the home
 * keeps its evergreen copy.
 *
 * Sections, top to bottom: hero · TL;DR (§3.1) · Loom video (§3.2) ·
 * founder note (§3.3) · discount card (§3.4 Version B) · 6-question FAQ
 * (§5). After the launch window (LAUNCH_END_AT) the page still renders but
 * swaps the discount card for an "expired" stub so old PH links don't 404.
 */
export function Launch() {
  const t = useT();
  const lp = useLp();
  const active = isLaunchActive();
  const launchVideoUrl = t.launch.video.embedUrl.trim();
  const hasLaunchVideo = hasLaunchVideoEmbedUrl(launchVideoUrl);

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={t.seo.launch.title}
        description={t.seo.launch.description}
        path="/launch"
        ogImage={t.seo.launch.ogImage}
        schema="faqPage"
      />
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative bg-paper-ruled">
          <div className="mx-auto max-w-3xl px-6 pt-20 pb-16 sm:pt-28 text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-rec/30 bg-rec/5 px-3 py-1 text-xs font-medium text-rec">
              <span className="h-1.5 w-1.5 rounded-full bg-rec animate-pulse" />
              {t.launch.hero.eyebrow}
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-6xl font-semibold tracking-tightest leading-[1.05] text-ink dark:text-paper">
              {t.launch.hero.h1}
            </h1>
            <p className="mt-6 mx-auto max-w-xl text-lg sm:text-xl text-ink/70 dark:text-paper/70 leading-relaxed">
              {t.launch.hero.sub}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to={lp + "/?auth=signup&plan=free&ref=ph"}
                className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
              >
                {t.launch.hero.cta}
                <span className="text-xs opacity-70">{t.launch.hero.ctaSub}</span>
              </Link>
              <a
                href="#founder-note"
                className="inline-flex items-center rounded-full border border-ink/15 px-6 py-3 text-sm font-medium text-ink hover:bg-ink/5 transition-colors dark:border-paper/20 dark:text-paper dark:hover:bg-paper/5"
              >
                {t.launch.hero.ctaSecondary}
              </a>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <Reveal>
          <section className="border-t border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-16">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.launch.tldr.heading}
              </h2>
              <p className="mt-6 text-[17px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.launch.tldr.body}
              </p>
            </div>
          </section>
        </Reveal>

        {hasLaunchVideo && (
          <Reveal>
            <section className="border-t border-margin dark:border-paper/10 bg-ink/[0.03] dark:bg-paper/[0.03]">
              <div className="mx-auto max-w-3xl px-6 py-16">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                  {t.launch.video.heading}
                </h2>
                <p className="mt-3 text-ink/70 dark:text-paper/70">
                  {t.launch.video.sub}
                </p>
                <div className="mt-8 overflow-hidden rounded-2xl border border-ink/10 dark:border-paper/12 bg-ink/5 dark:bg-paper/5 aspect-video">
                  <iframe
                    src={launchVideoUrl}
                    title={t.launch.video.heading}
                    allow="fullscreen"
                    className="h-full w-full"
                  />
                </div>
                <p className="mt-3 text-sm text-ink/55 dark:text-paper/55">
                  <a
                    href={launchVideoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-ink/30 underline-offset-4 hover:decoration-ink/65 dark:decoration-paper/35 dark:hover:decoration-paper/75"
                  >
                    {t.launch.video.caption}
                  </a>
                </p>
              </div>
            </section>
          </Reveal>
        )}

        {/* Founder note */}
        <Reveal>
          <section
            id="founder-note"
            className="border-t border-margin dark:border-paper/10 scroll-mt-20"
          >
            <div className="mx-auto max-w-3xl px-6 py-16">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.launch.founderNote.heading}
              </h2>
              <p className="mt-6 text-[17px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.launch.founderNote.body}
              </p>
              <p className="mt-4 font-medium text-ink dark:text-paper">
                {t.launch.founderNote.signature}
              </p>
            </div>
          </section>
        </Reveal>

        {/* Discount card */}
        <Reveal>
          <section className="border-t border-margin dark:border-paper/10 bg-rec/[0.04]">
            <div className="mx-auto max-w-3xl px-6 py-16">
              {active ? (
                <div className="rounded-2xl border border-rec/30 bg-paper p-8 dark:bg-ink dark:border-rec/40 sm:p-10">
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-rec">
                    {t.launch.discount.eyebrow}
                  </div>
                  <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                    {t.launch.discount.headline}
                  </h2>
                  <div className="mt-6 flex items-baseline gap-3">
                    <div className="font-display text-4xl font-semibold text-ink dark:text-paper">
                      {t.launch.discount.price}
                    </div>
                    <div className="text-ink/55 dark:text-paper/55 line-through">
                      {t.launch.discount.priceWas}
                    </div>
                  </div>
                  <p className="mt-4 text-ink/75 dark:text-paper/75 leading-relaxed">
                    {t.launch.discount.sub}
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="inline-flex items-center gap-2 rounded-lg border border-ink/15 dark:border-paper/15 px-3 py-2 font-mono text-sm">
                      <span className="text-ink/55 dark:text-paper/55">
                        {t.launch.discount.codeLabel}
                      </span>
                      <span className="font-semibold text-ink dark:text-paper">
                        {t.launch.discount.code}
                      </span>
                    </div>
                    <Link
                      to={lp + "/pricing?ref=ph&code=HUNT30"}
                      className="inline-flex items-center gap-2 rounded-full bg-rec px-6 py-3 text-sm font-medium text-paper hover:bg-rec/90 transition-colors"
                    >
                      {t.launch.discount.button}
                      <span className="text-xs opacity-80">{t.launch.discount.buttonSub}</span>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-ink/10 bg-paper p-8 dark:bg-ink dark:border-paper/10 sm:p-10 text-center">
                  <p className="text-ink/70 dark:text-paper/70">
                    The Product Hunt launch ran 2026-05-14 → 2026-05-21. Pricing is back on
                    the regular plan now —{" "}
                    <Link
                      to={lp + "/pricing"}
                      className="underline decoration-ink/30 underline-offset-4 hover:decoration-ink/65 dark:decoration-paper/35 dark:hover:decoration-paper/75"
                    >
                      see current plans
                    </Link>
                    .
                  </p>
                </div>
              )}
            </div>
          </section>
        </Reveal>

        {/* FAQ */}
        <Reveal>
          <section className="border-t border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-16">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.launch.faq.heading}
              </h2>
              <ul className="mt-8 divide-y divide-ink/8 dark:divide-paper/10 border-y border-ink/10 dark:border-paper/10">
                {t.launch.faq.items.map((item) => (
                  <li key={item.q}>
                    <details className="group">
                      <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-[17px] font-medium text-ink dark:text-paper list-none">
                        <span>{item.q}</span>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          className="mt-1.5 shrink-0 text-ink/50 dark:text-paper/50 transition-transform group-open:rotate-180"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 5l4 4 4-4" />
                        </svg>
                      </summary>
                      <p className="pb-5 pr-8 text-[15px] leading-relaxed text-ink/75 dark:text-paper/75">
                        {item.a}
                      </p>
                    </details>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>
      </main>

      <Footer />
    </div>
  );
}
